import { useState } from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemText, ListItemIcon,
  Checkbox, Chip, Stack, Button, Divider, TextField, Select, MenuItem,
  FormControl, InputLabel, Collapse,
  ToggleButtonGroup, ToggleButton, IconButton, Tooltip,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import Iconify from '../../../../components/iconify';
import { getSensorType } from '../../../../services/localizationService';

const OPERATORS = [
  { value: 'greater', label: 'Greater Than', symbol: '>' },
  { value: 'lesser', label: 'Less Than', symbol: '<' },
  { value: 'equal', label: 'Equal', symbol: '=' },
];

export default function RulesPickerWidget({
  rules = [], activeRules = [], recordings = [],
  // Scoped props
  globalRules = [], globalActiveRuleIds = [],
  tabRules = [], tabActiveRuleIds = [],
  onToggleGlobalRule, onToggleTabRule, onCreateRule,
  // Legacy props
  onToggleRule,
  // All tabs data for showing other tabs' rules
  widgetConfig,
}) {
  const [creating, setCreating] = useState(false);
  const [sensorType, setSensorType] = useState('');
  const [operator, setOperator] = useState('');
  const [value, setValue] = useState('');
  const [name, setName] = useState('');
  const [ruleScope, setRuleScope] = useState('tab'); // 'global' | 'tab'

  const globalActiveSet = new Set(globalActiveRuleIds);
  const tabActiveSet = new Set(tabActiveRuleIds);

  // All rules that can be toggled globally (API rules + workspace-local global rules)
  const globalRulePool = rules.filter((r) => !tabRules.find((tr) => tr.id === r.id));
  // Tab-only rules
  const tabRulePool = tabRules;

  // Other tabs' rules from widgetConfig (passed from WorkspaceGrid)
  const otherTabsRules = widgetConfig?.otherTabsRules || [];

  const allSensorTypes = [...new Set(
    recordings.flatMap((rec) =>
      Object.keys(rec.data || {}).map((k) => k.split(':').pop())
    ).filter((t) => !['altitude', 'latitude', 'longitude'].includes(t.toLowerCase()))
  )];

  const resetForm = () => {
    setSensorType('');
    setOperator('');
    setValue('');
    setName('');
    setRuleScope('tab');
    setCreating(false);
  };

  const handleCreate = () => {
    if (!sensorType || !operator || !value || Number.isNaN(parseFloat(value))) return;
    const op = OPERATORS.find((o) => o.value === operator);
    const rule = {
      id: `${uuidv4()}:${sensorType}`,
      name: name || `${getSensorType(sensorType)} ${op?.symbol || ''} ${value}`,
      sensorType,
      operator,
      value: parseFloat(value),
      scope: ruleScope,
      workspaceOnly: true,
    };
    onCreateRule(rule);
    resetForm();
  };

  const handleToggle = (rule, isGlobal) => {
    if (isGlobal) {
      (onToggleGlobalRule || onToggleRule)?.(rule);
    } else {
      (onToggleTabRule || onToggleRule)?.(rule);
    }
  };

  const renderRuleSecondary = (rule) =>
    `${getSensorType(rule.sensorType)} ${OPERATORS.find((o) => o.value === rule.operator)?.symbol || rule.operator} ${rule.value}`;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Active rules chips */}
      {activeRules.length > 0 && (
        <Stack direction="row" sx={{ px: 1, pt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
          {activeRules.map((rule) => {
            const isGlobal = globalActiveSet.has(rule.id);
            return (
              <Chip
                key={rule.id}
                label={rule.name}
                size="small"
                color="error"
                variant={isGlobal ? 'filled' : 'outlined'}
                onDelete={() => handleToggle(rule, isGlobal)}
              />
            );
          })}
        </Stack>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {/* Current Tab Rules Section */}
        <Typography variant="caption" fontWeight="bold" sx={{ px: 1, pt: 1, display: 'block', color: 'text.secondary' }}>
          Tab Rules
        </Typography>
        <List dense sx={{ py: 0 }}>
          {tabRulePool.map((rule) => (
            <ListItemButton key={rule.id} onClick={() => handleToggle(rule, false)} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox edge="start" checked={tabActiveSet.has(rule.id)} size="small" tabIndex={-1} disableRipple />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{rule.name}</span>
                  </Stack>
                }
                secondary={renderRuleSecondary(rule)}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Tooltip title="Move to workspace scope" placement="left">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Change scope from tab to global
                    if (onCreateRule) {
                      onCreateRule({ ...rule, scope: 'global', _promoteFromTab: true });
                    }
                  }}
                  sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}
                >
                  <Iconify icon="eva:globe-outline" width={16} />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          ))}
          {tabRulePool.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, display: 'block' }}>
              No tab rules
            </Typography>
          )}
        </List>

        <Divider sx={{ my: 0.5 }} />

        {/* Workspace Rules Section */}
        <Typography variant="caption" fontWeight="bold" sx={{ px: 1, display: 'block', color: 'text.secondary' }}>
          Workspace Rules (always active)
        </Typography>
        <List dense sx={{ py: 0 }}>
          {globalRulePool.map((rule) => (
            <ListItemButton key={rule.id} onClick={() => handleToggle(rule, true)} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox edge="start" checked={globalActiveSet.has(rule.id)} size="small" tabIndex={-1} disableRipple />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{rule.name}</span>
                    <Chip label="workspace" size="small" variant="outlined" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
                  </Stack>
                }
                secondary={renderRuleSecondary(rule)}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Tooltip title="Move to tab scope" placement="left">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Change scope from global to tab
                    if (onCreateRule) {
                      onCreateRule({ ...rule, scope: 'tab', _demoteFromGlobal: true });
                    }
                  }}
                  sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}
                >
                  <Iconify icon="eva:bookmark-outline" width={16} />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          ))}
          {globalRulePool.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, display: 'block' }}>
              No workspace rules
            </Typography>
          )}
        </List>

        {/* Other tabs' rules (visible but disabled) */}
        {otherTabsRules.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            {otherTabsRules.map((tabInfo) => (
              tabInfo.rules.length > 0 && (
                <Box key={tabInfo.tabId}>
                  <Typography variant="caption" fontWeight="bold" sx={{ px: 1, pt: 0.5, display: 'block', color: 'text.disabled' }}>
                    {tabInfo.tabName}
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {tabInfo.rules.map((rule) => (
                      <ListItemButton key={rule.id} disabled sx={{ py: 0.25, opacity: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox edge="start" checked={false} size="small" tabIndex={-1} disableRipple disabled />
                        </ListItemIcon>
                        <ListItemText
                          primary={rule.name}
                          secondary={renderRuleSecondary(rule)}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )
            ))}
          </>
        )}
      </Box>

      <Divider />

      {/* Create rule form */}
      <Collapse in={creating}>
        <Stack spacing={1} sx={{ p: 1 }}>
          {/* Scope selector */}
          <ToggleButtonGroup
            value={ruleScope}
            exclusive
            onChange={(_, v) => { if (v) setRuleScope(v); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.7rem', textTransform: 'none' } }}
          >
            <ToggleButton value="tab">Tab</ToggleButton>
            <ToggleButton value="global">Workspace</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Sensor</InputLabel>
              <Select value={sensorType} label="Sensor" onChange={(e) => setSensorType(e.target.value)}>
                {allSensorTypes.map((t) => (
                  <MenuItem key={t} value={t}>{getSensorType(t)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Op</InputLabel>
              <Select value={operator} label="Op" onChange={(e) => setOperator(e.target.value)}>
                {OPERATORS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label="Value" value={value} onChange={(e) => setValue(e.target.value)} sx={{ width: 80 }} />
          </Stack>
          <TextField size="small" label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={sensorType && operator && value ? `${getSensorType(sensorType)} ${OPERATORS.find((o) => o.value === operator)?.symbol || ''} ${value}` : ''}
          />
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
            <Button size="small" onClick={resetForm}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleCreate}
              disabled={!sensorType || !operator || !value || Number.isNaN(parseFloat(value))}
            >
              Create
            </Button>
          </Stack>
        </Stack>
      </Collapse>

      {!creating && (
        <Button
          size="small"
          startIcon={<Iconify icon="eva:plus-fill" width={16} />}
          onClick={() => setCreating(true)}
          sx={{ m: 0.5 }}
        >
          Create Rule
        </Button>
      )}
    </Box>
  );
}
